/*
 * Predictive search for the header search drawer (native replacement for the
 * Boost instant-search panel). Wraps the existing drawer form: on input it
 * fetches Shopify's predictive search section render (section_id=
 * predictive-search) and injects the panel below the form. The drawer shell
 * itself (open/close/focus trap) is handled by the theme's header component.
 *
 * Keyboard support follows the combobox pattern: focus stays in the input;
 * ArrowUp/ArrowDown move an aria-activedescendant selection across the
 * rendered [role="option"] items, Enter opens the selection, Escape clears.
 */
class PredictiveSearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-header-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.form = this.querySelector('form');
    if (!this.input || !this.results || !this.form) return;

    this.searchUrl = this.dataset.url || '/search/suggest';
    this.abortController = null;
    this.debounceTimer = null;
    this.selectedIndex = -1;

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.onSearch(), 300);
    });
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.form.addEventListener('submit', (event) => {
      if (!this.query) event.preventDefault();
    });
  }

  get query() {
    return this.input.value.trim();
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  onSearch() {
    const query = this.query;
    if (this.abortController) this.abortController.abort();
    if (!query) {
      this.close();
      return;
    }

    this.abortController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query,product,collection,page,article',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search',
    });

    fetch(`${this.searchUrl}?${params}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        this.results.removeAttribute('aria-busy');
        this.selectedIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        this.input.setAttribute('aria-expanded', this.results.innerHTML.trim() ? 'true' : 'false');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error); // eslint-disable-line no-console
        this.results.removeAttribute('aria-busy');
      });
  }

  onKeydown(event) {
    const options = this.options;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!options.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.select((this.selectedIndex + step + options.length) % options.length, options);
        break;
      }
      case 'Enter': {
        const selected = options[this.selectedIndex];
        const link = selected && selected.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          window.location.assign(link.href);
        }
        break;
      }
      case 'Escape':
        if (this.results.innerHTML.trim()) {
          // First Escape clears the panel; the drawer's own handler closes on the next.
          event.stopPropagation();
          this.close();
        }
        break;
      default:
    }
  }

  select(index, options) {
    options.forEach((option) => {
      option.setAttribute('aria-selected', 'false');
      option.classList.remove('is-selected');
    });
    this.selectedIndex = index;
    const option = options[index];
    option.setAttribute('aria-selected', 'true');
    option.classList.add('is-selected');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  close() {
    if (this.abortController) this.abortController.abort();
    this.results.innerHTML = '';
    this.results.removeAttribute('aria-busy');
    this.selectedIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
